import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api } from '@/api';
import { toast } from 'sonner';
import usePermissions from '@/hooks/usePermissions';
import { 
  Plus, Search, Filter, FileText, AlertCircle, CheckCircle, 
  Clock, ArrowRight, Building2, Calendar, AlertTriangle,
  Upload, Sparkles, Loader2, FileImage, FileScan
} from 'lucide-react';
import PrintScheduleButton from '@/components/PrintScheduleButton';

const SI_STATUSES = [
  'Draft', 'Captured', 'Distributed for Internal Comment', 
  'Under Consultant Clarification', 'Closed', 'Converted to RFI', 'Converted to Notice'
];

const SI_DISCIPLINES = [
  'Architectural', 'Structural', 'Electrical', 'Mechanical', 'Wet Services',
  'Fire', 'Lifts', 'Civil', 'Interior', 'Landscaping', 'General', 'Other'
];

const getStatusColor = (status) => {
  switch (status) {
    case 'Draft': return 'bg-slate-100 text-slate-700 border-slate-200';
    case 'Captured': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'Distributed for Internal Comment': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'Under Consultant Clarification': return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'Closed': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'Converted to RFI': return 'bg-cyan-100 text-cyan-700 border-cyan-200';
    case 'Converted to Notice': return 'bg-orange-100 text-orange-700 border-orange-200';
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

export default function SIList({ projectId: propProjectId, initialFilter = '', project }) {
  const { projectId: routeProjectId } = useParams();
  const projectId = propProjectId || routeProjectId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const permissions = usePermissions();
  
  const [sis, setSis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Initialize filter from URL params or prop
  const urlFilter = searchParams.get('filter') || initialFilter;
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [filterOverdue, setFilterOverdue] = useState(urlFilter === 'overdue');
  const [filterAwaiting, setFilterAwaiting] = useState(urlFilter === 'awaiting_action' || urlFilter === 'awaiting');
  const [filterCritical, setFilterCritical] = useState(urlFilter === 'critical');
  const [filterOpen, setFilterOpen] = useState(urlFilter === 'open');
  const [consultants, setConsultants] = useState([]);
  
  // AI Extraction state
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [siRes, teamRes] = await Promise.all([
        api.siteInstructions.getByProject(projectId),
        api.professionalTeam.getAll(projectId).catch(() => ({ data: [] }))
      ]);
      setSis(siRes.data);
      setConsultants(teamRes.data);
    } catch (error) {
      toast.error('Failed to load Site Instructions');
    } finally {
      setLoading(false);
    }
  };

  const filteredSis = sis.filter(si => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matches = 
        si.si_number?.toLowerCase().includes(query) ||
        si.si_title?.toLowerCase().includes(query) ||
        si.si_description?.toLowerCase().includes(query);
      if (!matches) return false;
    }
    
    // Status filter
    if (filterStatus && si.status !== filterStatus) return false;
    
    // Discipline filter
    if (filterDiscipline && si.discipline !== filterDiscipline) return false;
    
    // Open filter (not closed or converted)
    if (filterOpen) {
      if (['Closed', 'Converted to RFI', 'Converted to Notice'].includes(si.status)) return false;
    }
    
    // Overdue filter
    if (filterOverdue) {
      const dueDate = si.due_date_for_clarification ? new Date(si.due_date_for_clarification) : null;
      const isOverdue = dueDate && dueDate < new Date() && 
        ['Distributed for Internal Comment', 'Under Consultant Clarification'].includes(si.status);
      if (!isOverdue) return false;
    }
    
    // Awaiting action filter
    if (filterAwaiting) {
      if (!['Distributed for Internal Comment', 'Under Consultant Clarification'].includes(si.status)) return false;
    }
    
    // Critical filter (items older than 14 days)
    if (filterCritical) {
      const ageDays = si.created_at ? Math.floor((new Date() - new Date(si.created_at)) / (1000 * 60 * 60 * 24)) : 0;
      const isCritical = ageDays > 14 && !['Closed', 'Converted to RFI', 'Converted to Notice'].includes(si.status);
      if (!isCritical) return false;
    }
    
    return true;
  });

  const getConsultantName = (consultantId) => {
    const consultant = consultants.find(c => c.id === consultantId);
    if (!consultant) return 'Unknown';
    // Get primary contact name or company name
    const contactName = consultant.primary_contact 
      ? `${consultant.primary_contact.name || ''} ${consultant.primary_contact.surname || ''}`.trim()
      : '';
    return contactName || consultant.company_name || 'Unknown';
  };

  // AI Extraction handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image (JPEG, PNG, WebP) or PDF file');
        return;
      }
      setSelectedFile(file);
      setExtractionResult(null);
    }
  };

  const handleExtractAndCreate = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.siteInstructions.extractAndCreate(projectId, formData);
      
      if (response.data.success) {
        setExtractionResult(response.data);
        toast.success('SI extracted and created successfully!');
        
        // Navigate to edit the created SI
        setTimeout(() => {
          navigate(`/projects/${projectId}/si/${response.data.si_id}`);
        }, 1500);
      } else {
        toast.error(response.data.error || 'Extraction failed');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error(error.response?.data?.detail || 'Failed to extract SI data');
    } finally {
      setExtracting(false);
    }
  };

  const handleExtractOnly = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setExtracting(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const fileType = selectedFile.type.includes('pdf') ? 'pdf' : 'image';
        
        const response = await api.siteInstructions.extractFromDocument(
          base64,
          fileType,
          selectedFile.type
        );
        
        if (response.data.success) {
          setExtractionResult(response.data);
          toast.success('Data extracted successfully!');
        } else {
          toast.error(response.data.error || 'Extraction failed');
        }
        setExtracting(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error('Failed to extract SI data');
      setExtracting(false);
    }
  };

  const isOverdue = (si) => {
    // Check clarification due date
    if (si.clarification_due_date && si.status === 'Under Consultant Clarification') {
      return new Date(si.clarification_due_date) < new Date();
    }
    // Check general due date
    const dueDate = si.due_date_for_clarification ? new Date(si.due_date_for_clarification) : null;
    return dueDate && dueDate < new Date() && 
      ['Distributed for Internal Comment', 'Under Consultant Clarification'].includes(si.status);
  };

  const getAgeDays = (si) => {
    const created = new Date(si.created_at);
    const now = new Date();
    return Math.floor((now - created) / (1000 * 60 * 60 * 24));
  };

  const getAgeCategory = (si) => {
    const days = getAgeDays(si);
    if (days <= 3) return { label: 'New', color: 'bg-emerald-100 text-emerald-700' };
    if (days <= 7) return { label: 'Normal', color: 'bg-blue-100 text-blue-700' };
    if (days <= 14) return { label: 'Aging', color: 'bg-amber-100 text-amber-700' };
    return { label: 'Critical', color: 'bg-red-100 text-red-700' };
  };

  const getClarificationDaysRemaining = (si) => {
    if (!si.clarification_due_date) return null;
    const due = new Date(si.clarification_due_date);
    const now = new Date();
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Calculate stats
  const stats = {
    total: filteredSis.length,
    awaitingComment: filteredSis.filter(s => s.status === 'Distributed for Internal Comment').length,
    awaitingClarification: filteredSis.filter(s => s.status === 'Under Consultant Clarification').length,
    overdue: filteredSis.filter(s => isOverdue(s)).length,
    critical: filteredSis.filter(s => getAgeDays(s) > 14 && !['Closed', 'Converted to RFI', 'Converted to Notice'].includes(s.status)).length
  };
  
  // Get active filter name for display
  const getActiveFilterName = () => {
    if (filterOpen) return 'Open Items';
    if (filterOverdue) return 'Overdue Items';
    if (filterAwaiting) return 'Awaiting Action';
    if (filterCritical) return 'Critical Items (14+ days)';
    return null;
  };
  
  const activeFilterName = getActiveFilterName();
  
  // Clear all filters
  const clearFilters = () => {
    setFilterOpen(false);
    setFilterOverdue(false);
    setFilterAwaiting(false);
    setFilterCritical(false);
    setFilterStatus('');
    setFilterDiscipline('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading Site Instructions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Filter Banner */}
      {activeFilterName && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              Filtered by: <span className="font-bold">{activeFilterName}</span>
            </span>
            <Badge className="bg-blue-100 text-blue-700">{filteredSis.length} items</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-blue-600 hover:text-blue-800">
            Clear Filter
          </Button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-barlow font-semibold uppercase">Site Instructions</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Manage site instructions from consultants</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <PrintScheduleButton moduleType="SiteInstruction" data={sis} project={project} />
          {permissions.canCreateSI() && (
            <>
              <Button
                onClick={() => setExtractDialogOpen(true)}
                variant="outline"
                size="sm"
                className="rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3 border-purple-500 text-purple-600 hover:bg-purple-50"
                data-testid="extract-si-button"
              >
                <Sparkles className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Upload & Extract</span>
              </Button>
              <Button
                onClick={() => navigate(`/projects/${projectId}/si/create`)}
                size="sm"
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                data-testid="create-si-button"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">New Site Instruction</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* AI Extraction Dialog */}
      <Dialog open={extractDialogOpen} onOpenChange={setExtractDialogOpen}>
        <DialogContent className="max-w-2xl" aria-describedby="extract-dialog-description">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              AI-Powered SI Extraction
            </DialogTitle>
            <DialogDescription id="extract-dialog-description" className="text-slate-600">
              Upload a scanned Site Instruction document and our AI will extract the data automatically
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* File Upload Area */}
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                selectedFile 
                  ? 'border-purple-400 bg-purple-50' 
                  : 'border-slate-300 hover:border-purple-400 hover:bg-purple-50/50'
              }`}
              onClick={() => fileInputRef.current?.click()}
              style={{ cursor: 'pointer' }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="extract-file-input"
              />
              
              {selectedFile ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2">
                    {selectedFile.type.includes('pdf') ? (
                      <FileScan className="h-10 w-10 text-purple-600" />
                    ) : (
                      <FileImage className="h-10 w-10 text-purple-600" />
                    )}
                  </div>
                  <p className="font-medium text-slate-900">{selectedFile.name}</p>
                  <p className="text-sm text-slate-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB • Click to change
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-10 w-10 text-slate-400 mx-auto" />
                  <p className="font-medium text-slate-600">
                    Drop a scanned SI here or click to browse
                  </p>
                  <p className="text-sm text-slate-400">
                    Supports: JPEG, PNG, WebP, PDF
                  </p>
                </div>
              )}
            </div>

            {/* Extraction Result */}
            {extractionResult && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  Extraction Results
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">SI Number:</span>
                    <span className="ml-2 font-medium">{extractionResult.extracted_data?.si_number || 'Not found'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Title:</span>
                    <span className="ml-2 font-medium">{extractionResult.extracted_data?.si_title || 'Not found'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Issue Date:</span>
                    <span className="ml-2 font-medium">{extractionResult.extracted_data?.issue_date || 'Not found'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Confidence:</span>
                    <span className={`ml-2 font-medium ${
                      (extractionResult.extracted_data?.extraction_confidence || 0) >= 70 
                        ? 'text-emerald-600' 
                        : 'text-amber-600'
                    }`}>
                      {extractionResult.extracted_data?.extraction_confidence || 0}%
                    </span>
                  </div>
                </div>
                
                {extractionResult.flags?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-xs font-medium text-amber-600 mb-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Review Needed:
                    </p>
                    <ul className="text-xs text-slate-600 space-y-0.5">
                      {extractionResult.flags.slice(0, 3).map((flag, i) => (
                        <li key={i}>• {flag}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleExtractOnly}
                disabled={!selectedFile || extracting}
                className="flex-1 rounded-sm"
              >
                {extracting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Preview Extraction
              </Button>
              <Button
                onClick={handleExtractAndCreate}
                disabled={!selectedFile || extracting}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-sm"
                data-testid="extract-create-button"
              >
                {extracting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Extract & Create SI
              </Button>
            </div>

            <p className="text-xs text-slate-400 text-center">
              AI will extract SI number, dates, description, and other fields from your document
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 sm:items-center">
          <div className="flex-1 min-w-0 sm:min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search SI number, title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-si"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <Select value={filterStatus || "all"} onValueChange={(val) => setFilterStatus(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-[200px] rounded-sm" data-testid="filter-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {SI_STATUSES.map(status => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterDiscipline || "all"} onValueChange={(val) => setFilterDiscipline(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-[180px] rounded-sm" data-testid="filter-discipline">
                <SelectValue placeholder="All Disciplines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Disciplines</SelectItem>
                {SI_DISCIPLINES.map(disc => (
                  <SelectItem key={disc} value={disc}>{disc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => setFilterOverdue(!filterOverdue)}
              variant={filterOverdue ? "default" : "outline"}
              size="sm"
              className={`rounded-sm ${filterOverdue ? 'bg-red-600 hover:bg-red-700' : ''}`}
              data-testid="filter-overdue"
            >
              <AlertTriangle className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Overdue Only</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Total Open</p>
          <p className="text-xl sm:text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-amber-600">Awaiting Comment</p>
          <p className="text-xl sm:text-2xl font-bold text-amber-600">{stats.awaitingComment}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-cyan-600">Awaiting Clarif.</p>
          <p className="text-xl sm:text-2xl font-bold text-cyan-600">{stats.awaitingClarification}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-red-600">Overdue</p>
          <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.overdue}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-3 sm:p-4 col-span-2 sm:col-span-1 border-l-4 border-l-red-500">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-red-700">Critical (14+ days)</p>
          <p className="text-xl sm:text-2xl font-bold text-red-700">{stats.critical}</p>
        </div>
      </div>

      {/* SI List */}
      {filteredSis.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-12 text-center">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="text-lg font-barlow font-bold uppercase text-slate-500 mb-2">
            {sis.length === 0 ? 'No Site Instructions Yet' : 'No Matching Results'}
          </h3>
          <p className="text-sm text-slate-400 mb-4">
            {sis.length === 0 
              ? 'Create a new Site Instruction to get started' 
              : 'Try adjusting your filters'}
          </p>
          {sis.length === 0 && permissions.canCreateSI() && (
            <Button
              onClick={() => navigate(`/projects/${projectId}/si/create`)}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
              data-testid="create-si-empty-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Site Instruction
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSis.map((si) => (
            <div
              key={si.id}
              onClick={() => navigate(`/projects/${projectId}/si/${si.id}`)}
              className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 sm:p-6 hover:shadow-md transition-all cursor-pointer group"
              data-testid={`si-item-${si.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="font-mono font-bold text-base sm:text-lg text-slate-900">{si.si_number}</span>
                    <Badge className={`${getStatusColor(si.status)} text-xs`}>
                      {si.status}
                    </Badge>
                    {/* Age indicator */}
                    <Badge className={`${getAgeCategory(si).color} text-xs`}>
                      {getAgeDays(si)}d {getAgeCategory(si).label}
                    </Badge>
                    {isOverdue(si) && (
                      <Badge className="bg-red-100 text-red-700 border-red-200 text-xs animate-pulse">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Overdue
                      </Badge>
                    )}
                    <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                      {si.discipline}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-base sm:text-lg mb-1">{si.si_title}</h3>
                  {si.si_description && (
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">{si.si_description}</p>
                  )}
                  <div className="flex items-center gap-3 sm:gap-4 text-xs text-slate-500 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {getConsultantName(si.issued_by_consultant_id)}
                    </span>
                    {si.issue_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Issued: {new Date(si.issue_date).toLocaleDateString()}
                      </span>
                    )}
                    {/* Clarification due date */}
                    {si.clarification_due_date && si.status === 'Under Consultant Clarification' && (
                      <span className={`flex items-center gap-1 ${getClarificationDaysRemaining(si) < 0 ? 'text-red-600 font-medium' : getClarificationDaysRemaining(si) <= 2 ? 'text-amber-600' : ''}`}>
                        <Clock className="h-3 w-3" />
                        {getClarificationDaysRemaining(si) < 0 
                          ? `${Math.abs(getClarificationDaysRemaining(si))}d overdue`
                          : `${getClarificationDaysRemaining(si)}d remaining`}
                      </span>
                    )}
                    {si.cost_impact_expected === 'Yes' && (
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                        Cost Impact
                      </Badge>
                    )}
                    {si.time_impact_expected === 'Yes' && (
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                        Time Impact
                      </Badge>
                    )}
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600 transition-colors" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
